import { useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { AddressStructured } from '@bumble/app/__generated__/Api';
import { Box, Button, Divider, Grid, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface RouterCpeInfoEditProps {
  onSubmit: () => void;
  cpeId: string;
  cpeName: string;
  shippingAddress?: AddressStructured;
  siteAddress?: AddressStructured;
  description?: string;
  providerServiceID?: string;
  clientCircuitID?: string;
  carrierInfo?: string;
  localSupportName?: string;
  localSupportEmail?: string;
  localSupportPhone?: string;
  pingEmailNotification?: string;
  pingWebhookNotification?: string;
  shippingContactName?: string;
  shippingContactEmail?: string;
  shippingContactPhone?: string;
}

const emptyAddress = (): AddressStructured => ({
  street_address: '',
  city: '',
  state_province: '',
  postal_code: '',
  country: '',
});

const useAddressState = (initial?: AddressStructured) => {
  const [streetAddress, setStreetAddress] = useState(initial?.street_address ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [stateProvince, setStateProvince] = useState(initial?.state_province ?? '');
  const [postalCode, setPostalCode] = useState(initial?.postal_code ?? '');
  const [country, setCountry] = useState(initial?.country ?? '');

  const value: AddressStructured = { street_address: streetAddress, city, state_province: stateProvince, postal_code: postalCode, country };

  return { streetAddress, setStreetAddress, city, setCity, stateProvince, setStateProvince, postalCode, setPostalCode, country, setCountry, value };
};

interface AddressFieldsProps {
  label: string;
  state: ReturnType<typeof useAddressState>;
}

const AddressFields = ({ label, state }: AddressFieldsProps) => (
  <>
    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>{label}</Typography>
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Street Address"
          variant="outlined"
          value={state.streetAddress}
          onChange={(e) => state.setStreetAddress(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="City"
          variant="outlined"
          value={state.city}
          onChange={(e) => state.setCity(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="State / Province"
          variant="outlined"
          value={state.stateProvince}
          onChange={(e) => state.setStateProvince(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Postal Code"
          variant="outlined"
          value={state.postalCode}
          onChange={(e) => state.setPostalCode(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Country"
          variant="outlined"
          value={state.country}
          onChange={(e) => state.setCountry(e.target.value)}
        />
      </Grid>
    </Grid>
  </>
);

export const RouterCpeInfoEdit = ({
    onSubmit, cpeId, cpeName, shippingAddress, siteAddress, description,
    shippingContactName, shippingContactEmail, shippingContactPhone, providerServiceID, clientCircuitID,
    carrierInfo, localSupportName, localSupportEmail, localSupportPhone, pingEmailNotification, pingWebhookNotification,
  }: RouterCpeInfoEditProps) => {
  const api = useApi();
  const [newCpeName, setNewCpeName] = useState<string>(cpeName);
  const [newDesc, setNewDesc] = useState<string | undefined>(description);
  const [newProviderServiceID, setNewProviderServiceID] = useState<string | undefined>(providerServiceID);
  const [newClientCircuitID, setNewClientCircuitID] = useState<string | undefined>(clientCircuitID);
  const [newCarrierInfo, setNewCarrierInfo] = useState<string | undefined>(carrierInfo);
  const [newLocalSupportName, setNewLocalSupportName] = useState<string | undefined>(localSupportName);
  const [newLocalSupportEmail, setNewLocalSupportEmail] = useState<string | undefined>(localSupportEmail);
  const [newLocalSupportPhone, setNewLocalSupportPhone] = useState<string | undefined>(localSupportPhone);
  const [newShippingContactName, setNewShippingContactName] = useState<string | undefined>(shippingContactName);
  const [newShippingContactEmail, setNewShippingContactEmail] = useState<string | undefined>(shippingContactEmail);
  const [newShippingContactPhone, setNewShippingContactPhone] = useState<string | undefined>(shippingContactPhone);

  const shippingState = useAddressState(shippingAddress);
  const siteState = useAddressState(siteAddress);

  const cpeInfoUpdatePost = useMutation(api.updateCpeInfoApiV1UpdateCpeInfoPost, {
    onSuccess() {
      toast.success('Updated CPE info successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    cpeInfoUpdatePost.mutate({
      cpe_id: cpeId,
      cpe_name: newCpeName,
      shipping_address: shippingState.value,
      site_address: siteState.value,
      provider_service_id: newProviderServiceID,
      client_circuit_id: newClientCircuitID,
      carrier_info: newCarrierInfo,
      local_support_contact_name: newLocalSupportName,
      local_support_contact_email: newLocalSupportEmail,
      local_support_contact_phone: newLocalSupportPhone,
      contact_name: newShippingContactName,
      contact_email: newShippingContactEmail,
      contact_phone: newShippingContactPhone,
      ping_email_notification: pingEmailNotification,
      ping_webhook_notification: pingWebhookNotification,
      ...(newDesc !== undefined && { description: newDesc }),
    });
  };

  return (
    <Box pt={2}>
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Device ID"
        variant="outlined"
        value={cpeId}
        disabled
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Name"
        variant="outlined"
        value={newCpeName}
        onChange={(e) => setNewCpeName(e.target.value)}
        autoFocus
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Provider Service ID"
        variant="outlined"
        value={newProviderServiceID}
        onChange={(e) => setNewProviderServiceID(e.target.value)}
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Client Circuit ID"
        variant="outlined"
        value={newClientCircuitID}
        onChange={(e) => setNewClientCircuitID(e.target.value)}
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Carrier Info"
        variant="outlined"
        value={newCarrierInfo}
        onChange={(e) => setNewCarrierInfo(e.target.value)}
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Description"
        variant="outlined"
        value={newDesc}
        onChange={(e) => setNewDesc(e.target.value)}
      />

      <Divider sx={{ mt: 4, mb: 4 }} />
      <Box sx={{ mb: 6 }}>
        <AddressFields label="Site Address" state={siteState} />
      </Box>

      <Divider sx={{ mb: 4 }} />
      <Box sx={{ mb: 6 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Local Support Contact</Typography>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Local Support Contact Name"
              variant="outlined"
              value={newLocalSupportName}
              onChange={(e) => setNewLocalSupportName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Local Support Contact Email"
              variant="outlined"
              value={newLocalSupportEmail}
              onChange={(e) => setNewLocalSupportEmail(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Local Support Contact Phone"
              variant="outlined"
              value={newLocalSupportPhone}
              onChange={(e) => setNewLocalSupportPhone(e.target.value)}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 4 }} />
      <Box sx={{ mb: 4 }}>
        <AddressFields label="Shipping Address" state={shippingState} />
      </Box>
      <Grid container spacing={4} sx={{ mt: 0 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Shipping Recipient Contact Name"
            variant="outlined"
            value={newShippingContactName}
            onChange={(e) => setNewShippingContactName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Shipping Recipient Contact Email"
            variant="outlined"
            value={newShippingContactEmail}
            onChange={(e) => setNewShippingContactEmail(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Shipping Recipient Contact Phone"
            variant="outlined"
            value={newShippingContactPhone}
            onChange={(e) => setNewShippingContactPhone(e.target.value)}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          variant="outlined"
          onClick={handleSubmit}
          disabled={!newCpeName}
        >
          Save CPE info
        </Button>
      </Box>
    </Box>
  );
};
